// MARCA | 27/03/2026 | Custom element for displaying recently viewed products in the empty cart state
import { RecentlyViewed } from "@theme/recently-viewed-products";
import { sectionRenderer } from "@theme/section-renderer";
// MARCA | 27/03/2026 | Import cart events for quick-add functionality
import { ThemeEvents, CartAddEvent } from "@theme/events";

/** @type {string} Section ID used by the Section Rendering API for empty cart recently viewed cards */
const SECTION_ID = "empty-cart-recently-viewed";

/**
 * Custom element that loads recently viewed products into the empty cart state.
 * Falls back to the sibling collections grid if no recently viewed products are available.
 * Extends HTMLElement directly (not Component) because the element starts empty —
 * Component's connectedCallback expects child structure (refs, shadow DOM) that isn't present here.
 *
 * @extends {HTMLElement}
 */
class EmptyCartRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.#loadProducts();
  }

  /**
   * MARCA | 27/03/2026 | Fetch recently viewed product cards via the Section Rendering API.
   * Reads product IDs from localStorage, builds a search URL, and injects
   * server-rendered cards that reuse the collection-card markup and styles.
   * If no recently viewed products exist, the fallback collections grid stays visible.
   */
  async #loadProducts() {
    const viewedProductIds = RecentlyViewed.getProducts();
    if (viewedProductIds.length === 0) return;

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set(
        "q",
        viewedProductIds
          .map((/** @type {string} */ id) => `id:${id}`)
          .join(" OR "),
      );
      url.searchParams.set("type", "product");

      const html = await sectionRenderer.getSectionHTML(SECTION_ID, false, url);
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const cards = doc.querySelectorAll(".cart-items__collection-card");
      if (cards.length === 0) return;

      const cardsByProductId = new Map();
      for (const card of cards) {
        if (card instanceof HTMLElement) {
          cardsByProductId.set(card.dataset.productId, card);
        }
      }

      for (const productId of viewedProductIds) {
        const card = cardsByProductId.get(productId);
        if (card) this.appendChild(card);
      }

      this.#bindAddButtons();

      const fallback = this.parentElement?.querySelector(
        ".cart-items__empty-collections--fallback",
      );
      if (fallback instanceof HTMLElement) fallback.hidden = true;

      this.hidden = false;
    } catch (error) {
      console.error("Failed to load recently viewed products:", error);
    }
  }

  #bindAddButtons() {
    this.querySelectorAll(".cart-items__add-button").forEach((button) => {
      button.addEventListener("click", (e) => this.#handleAdd(e));
    });
  }

  /**
   * MARCA | 27/03/2026 | Quick-add handler — mirrors the cart-upsell add-to-cart flow.
   * @param {Event} event
   */
  async #handleAdd(event) {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    const textEl = button.querySelector(".cart-items__add-button-text");
    const originalText = textEl?.textContent || "";
    if (textEl) textEl.textContent = "Adding...";

    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const routes = shopify?.routes?.root || "/";

      const response = await fetch(routes + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });

      if (!response.ok) throw new Error("Failed to add product");

      await response.json();

      const cartResponse = await fetch(routes + "cart.js");
      const cartData = await cartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(cartData, "empty-cart-recently-viewed", {
          itemCount: cartData.item_count,
          source: "empty-cart-recently-viewed",
        }),
      );

      if (textEl) textEl.textContent = "Added!";
      setTimeout(() => {
        button.disabled = false;
        if (textEl) textEl.textContent = originalText;
      }, 1500);
    } catch (error) {
      console.error("Failed to add product:", error);
      button.disabled = false;
      if (textEl) textEl.textContent = originalText;
    }
  }
}

if (!customElements.get("empty-cart-recently-viewed")) {
  customElements.define("empty-cart-recently-viewed", EmptyCartRecentlyViewed);
}
